import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import type { User as FirebaseUser } from 'firebase/auth';
import { db, hasFirebaseConfig } from '../lib/firebase';
import type { AppLanguage, User, UserSettings } from '../types';

const DEFAULT_SETTINGS: UserSettings = {
  dailyGoalMinutes: 10,
  trainingModeDefault: 'training',
  sound: true,
};

function isAppLanguage(value: unknown): value is AppLanguage {
  return value === 'uk' || value === 'en';
}

function toIsoString(value: unknown, fallback: string) {
  if (!value) {
    return fallback;
  }

  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybe = value as { toDate?: () => Date };
    if (typeof maybe.toDate === 'function') {
      return maybe.toDate().toISOString();
    }
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? fallback : date.toISOString();
  }

  return fallback;
}

function toBoolean(value: unknown, fallback: boolean) {
  return typeof value === 'boolean' ? value : fallback;
}

function toNumber(value: unknown, fallback: number) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
}

export function getDefaultUserSettings(): UserSettings {
  return { ...DEFAULT_SETTINGS };
}

function mapFirebaseUserToBaseUser(firebaseUser: FirebaseUser): User {
  const createdAt = firebaseUser.metadata.creationTime
    ? new Date(firebaseUser.metadata.creationTime).toISOString()
    : new Date().toISOString();
  const lastActiveAt = firebaseUser.metadata.lastSignInTime
    ? new Date(firebaseUser.metadata.lastSignInTime).toISOString()
    : new Date().toISOString();

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email ?? '',
    displayName: firebaseUser.displayName ?? 'VerbMaster User',
    photoUrl: firebaseUser.photoURL ?? '',
    createdAt,
    lastActiveAt,
    lang: 'uk',
    plan: 'free',
    settings: getDefaultUserSettings(),
  };
}

function mergeUserProfile(firebaseUser: FirebaseUser, data?: Record<string, unknown>): User {
  const baseUser = mapFirebaseUserToBaseUser(firebaseUser);
  const settingsSource = data?.settings as Record<string, unknown> | undefined;

  return {
    ...baseUser,
    email: typeof data?.email === 'string' && data.email ? data.email : baseUser.email,
    displayName:
      typeof data?.displayName === 'string' && data.displayName ? data.displayName : baseUser.displayName,
    photoUrl: typeof data?.photoUrl === 'string' ? data.photoUrl : baseUser.photoUrl,
    createdAt: toIsoString(data?.createdAt, baseUser.createdAt),
    lastActiveAt: toIsoString(data?.lastActiveAt, baseUser.lastActiveAt),
    lang: isAppLanguage(data?.lang) ? data.lang : baseUser.lang,
    plan: data?.plan === 'pro' ? 'pro' : 'free',
    settings: {
      dailyGoalMinutes: toNumber(settingsSource?.dailyGoalMinutes, baseUser.settings.dailyGoalMinutes),
      trainingModeDefault:
        settingsSource?.trainingModeDefault === 'test' ? 'test' : baseUser.settings.trainingModeDefault,
      sound: toBoolean(settingsSource?.sound, baseUser.settings.sound),
    },
  };
}

async function writeProfile(user: User) {
  if (!hasFirebaseConfig || !db) {
    return;
  }

  await setDoc(
    doc(db, 'users', user.uid),
    {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoUrl: user.photoUrl,
      createdAt: user.createdAt,
      lastActiveAt: user.lastActiveAt,
      lang: user.lang,
      plan: user.plan,
      settings: user.settings,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export async function ensureUserProfile(firebaseUser: FirebaseUser): Promise<User> {
  const baseUser = mapFirebaseUserToBaseUser(firebaseUser);

  if (!hasFirebaseConfig || !db) {
    return baseUser;
  }

  const profileRef = doc(db, 'users', firebaseUser.uid);
  const snapshot = await getDoc(profileRef);
  const mergedUser = mergeUserProfile(firebaseUser, snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : undefined);
  const nextUser = {
    ...mergedUser,
    email: firebaseUser.email ?? mergedUser.email,
    displayName: firebaseUser.displayName ?? mergedUser.displayName,
    photoUrl: firebaseUser.photoURL ?? mergedUser.photoUrl,
    lastActiveAt: baseUser.lastActiveAt,
  };

  await writeProfile(nextUser);
  return nextUser;
}

export async function updateUserSettings(user: User, updates: Partial<UserSettings>): Promise<User> {
  const nextUser: User = {
    ...user,
    settings: {
      ...user.settings,
      ...updates,
    },
  };

  await writeProfile(nextUser);
  return nextUser;
}

export async function updateUserLanguage(user: User, lang: AppLanguage): Promise<User> {
  const nextUser: User = {
    ...user,
    lang,
  };

  await writeProfile(nextUser);
  return nextUser;
}
